import { useState, useEffect, useMemo, useCallback, type ReactNode } from 'react';
import { StationObservationsContext, type StationObservationsContextType, type WeatherDataSource, type NormalizedWeatherData } from './stationObservationsTypes';
import { useEventSelection } from './useEventSelection';
import { useSelectedTimestamp } from './useSelectedTimestamp';
import { useTopBarStation } from './useTopBarStation';
import stationObservationsService, { type StationData } from '../api/stationObservationsService';
import tomorrowIoTimelineService, { type TioCurrentWeatherData } from '../api/tomorrowIoTimelineService';
import { getEventSource } from '../utils/eventSource';
export const StationObservationsProvider = ({ children }: { children: ReactNode }) => {
  const { selectedEvent } = useEventSelection();
  const { selectedTimestamp } = useSelectedTimestamp();
  const { topBarStationId } = useTopBarStation();
  
  // Station observations state
  const [observations, setObservations] = useState<StationData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // External stations data (provided by NowcastingPage/ObservationsPage to avoid duplicate API calls)
  const [externalStations, setExternalStations] = useState<StationData[] | null>(null);
  // Date range for finding latest observation in range (set by NowcastingPage/ObservationsPage)
  const [selectedDateRange, setSelectedDateRangeState] = useState<{ start: Date; end: Date } | null>(null);
  
  // Tomorrow.io API state
  const [tioData, setTioData] = useState<TioCurrentWeatherData | null>(null);
  const [tioLoading, setTioLoading] = useState(false);
  const [tioError, setTioError] = useState<string | null>(null);
  const [tioTimestamp, setTioTimestamp] = useState<Date | null>(null);
  
  // Data source selection
  const [dataSource, setDataSource] = useState<WeatherDataSource>('stations');
  
  // Refreshing state
  const [refreshing, setRefreshing] = useState(false);

  // Check if event is currently active - use status from API (computed with circuit timezone)
  const isTioAvailable = useMemo(() => {
    if (!selectedEvent) return false;
    return selectedEvent.status === 'Current';
  }, [selectedEvent]);

  // Check if observations are enabled (default to true for backward compatibility)
  const observationsEnabled = useMemo(() => {
    return selectedEvent?.observations_enabled !== false;
  }, [selectedEvent]);

  // Reset to stations if TIO becomes unavailable (only if observations are enabled)
  useEffect(() => {
    if (!isTioAvailable && dataSource === 'tio-api' && observationsEnabled) {
      setDataSource('stations');
    }
  }, [isTioAvailable, dataSource, observationsEnabled]);

  // Force dataSource to 'tio-api' when observations are disabled
  useEffect(() => {
    if (!observationsEnabled && dataSource === 'stations') {
      setDataSource('tio-api');
    }
  }, [observationsEnabled, dataSource]);

  // Helper: average the readings of multiple observations into a single synthetic observation.
  // Averages by field name (not index) because each station may have different reading_config order/length.
  const averageObservations = useCallback((
    observations: StationData['observations'],
    stations: StationData[],
    readingConfig: StationData['reading_config']
  ): { observation: StationData['observations'][0]; readingConfig: StationData['reading_config'] } => {
    // Build merged config: union of all fields from all stations (preserves order, adds missing from others)
    const seen = new Set(readingConfig.map(c => c.name));
    const mergedConfig = [...readingConfig];
    for (const station of stations) {
      for (const cfg of station.reading_config) {
        if (!seen.has(cfg.name)) {
          seen.add(cfg.name);
          mergedConfig.push(cfg);
        }
      }
    }

    const avgReadings: (number | null)[] = new Array(mergedConfig.length).fill(null);

    for (let i = 0; i < mergedConfig.length; i++) {
      const fieldName = mergedConfig[i].name;
      let sum = 0;
      let count = 0;
      for (let j = 0; j < observations.length; j++) {
        const station = stations[j];
        const obs = observations[j];
        const paramIndex = station.reading_config.findIndex(config => config.name === fieldName);
        if (paramIndex !== -1) {
          const val = obs.readings[paramIndex];
          if (val !== null && val !== undefined) {
            sum += val;
            count++;
          }
        }
      }
      avgReadings[i] = count > 0 ? sum / count : null;
    }

    // Use the latest timestamp among the averaged observations
    const latestObs = observations.reduce((latest, obs) =>
      new Date(obs.reading_timestamp).getTime() > new Date(latest.reading_timestamp).getTime() ? obs : latest
    );

    return {
      observation: {
        reading_timestamp: latestObs.reading_timestamp,
        car_wind_direction: null,
        readings: avgReadings,
      },
      readingConfig: mergedConfig,
    };
  }, []);

  // Helper: find observation closest to a single timestamp (for Forecast page etc.)
  const findClosestObservation = useCallback((stations: StationData[], timestamp: Date): StationData | null => {
    if (topBarStationId === 'avg') {
      // Average mode: find closest observation per station, then average them
      const closestPerStation: StationData['observations'] = [];
      const stationsWithData: StationData[] = [];
      let refConfig: StationData['reading_config'] | null = null;
      for (const station of stations) {
        if (station.observations.length === 0) continue;
        if (!refConfig) refConfig = station.reading_config;
        const closest = station.observations.reduce((c, obs) => {
          const ot = new Date(obs.reading_timestamp).getTime();
          const ct = new Date(c.reading_timestamp).getTime();
          const st = timestamp.getTime();
          return Math.abs(ot - st) < Math.abs(ct - st) ? obs : c;
        });
        closestPerStation.push(closest);
        stationsWithData.push(station);
      }
      if (closestPerStation.length === 0 || !refConfig) return null;
      const { observation, readingConfig: mergedConfig } = averageObservations(closestPerStation, stationsWithData, refConfig);
      return {
        station_reference: 'avg',
        observation_count: 1,
        reading_config: mergedConfig,
        observations: [observation],
      };
    }

    let station = stations.find(s => s.station_reference === topBarStationId);
    if (!station || station.observations.length === 0) {
      station = stations.find(s => s.observations.length > 0);
    }
    if (!station) return null;

    const closestObservation = station.observations.reduce((closest, obs) => {
      const obsTime = new Date(obs.reading_timestamp).getTime();
      const closestTime = new Date(closest.reading_timestamp).getTime();
      const selectedTime = timestamp.getTime();
      return Math.abs(obsTime - selectedTime) < Math.abs(closestTime - selectedTime) ? obs : closest;
    });

    return {
      station_reference: station.station_reference,
      observation_count: 1,
      reading_config: station.reading_config,
      observations: [closestObservation],
    };
  }, [topBarStationId, averageObservations]);

  // Helper: find the latest observation in range for a single station (with fallback)
  const findLatestForStation = useCallback((
    station: StationData,
    rangeStart: number,
    rangeEnd: number
  ): StationData['observations'][0] | null => {
    const inRange = station.observations.filter((obs) => {
      const t = new Date(obs.reading_timestamp).getTime();
      return t >= rangeStart && t <= rangeEnd;
    });

    if (inRange.length > 0) {
      return inRange.reduce((latest, obs) =>
        new Date(obs.reading_timestamp).getTime() > new Date(latest.reading_timestamp).getTime() ? obs : latest
      );
    }
    // Fallback: latest observation at or before range end
    const before = station.observations.filter((obs) => new Date(obs.reading_timestamp).getTime() <= rangeEnd);
    if (before.length === 0) return null;
    return before.reduce((latest, obs) =>
      new Date(obs.reading_timestamp).getTime() > new Date(latest.reading_timestamp).getTime() ? obs : latest
    );
  }, []);

  // Helper: find the latest (most recent) observation within the given range
  // If no observations fall in range, fall back to latest observation <= range end (avoids flicker on range updates)
  const findLatestInRange = useCallback((
    stations: StationData[],
    range: { start: Date; end: Date }
  ): StationData | null => {
    const rangeStart = range.start.getTime();
    const rangeEnd = range.end.getTime();

    if (topBarStationId === 'avg') {
      // Average mode: find latest per station, then average them
      const latestPerStation: StationData['observations'] = [];
      const stationsWithData: StationData[] = [];
      let refConfig: StationData['reading_config'] | null = null;
      for (const station of stations) {
        if (station.observations.length === 0) continue;
        if (!refConfig) refConfig = station.reading_config;
        const latest = findLatestForStation(station, rangeStart, rangeEnd);
        if (latest) {
          latestPerStation.push(latest);
          stationsWithData.push(station);
        }
      }
      if (latestPerStation.length === 0 || !refConfig) return null;
      const { observation, readingConfig: mergedConfig } = averageObservations(latestPerStation, stationsWithData, refConfig);
      return {
        station_reference: 'avg',
        observation_count: 1,
        reading_config: mergedConfig,
        observations: [observation],
      };
    }

    let station = stations.find(s => s.station_reference === topBarStationId);
    if (!station || station.observations.length === 0) {
      station = stations.find(s => s.observations.length > 0);
    }
    if (!station) return null;

    const latestObservation = findLatestForStation(station, rangeStart, rangeEnd);
    if (!latestObservation) return null;

    return {
      station_reference: station.station_reference,
      observation_count: 1,
      reading_config: station.reading_config,
      observations: [latestObservation],
    };
  }, [topBarStationId, averageObservations, findLatestForStation]);

  const setSelectedDateRange = useCallback((range: { start: Date; end: Date } | null) => {
    setSelectedDateRangeState(range);
  }, []);

  // Reset state when event changes
  useEffect(() => {
    setObservations(null);
    setExternalStations(null);
    setSelectedDateRangeState(null);
    setTioData(null);
    setTioTimestamp(null);
    setError(null);
    setTioError(null);
    // Set dataSource based on whether observations are enabled
    // If observations are disabled, force to 'tio-api'
    setDataSource(observationsEnabled ? 'stations' : 'tio-api');
  }, [selectedEvent, observationsEnabled]);

  // When external stations are provided, update observations based on range or timestamp.
  // When external stations are cleared (page switch), clear observations so cards reset.
  useEffect(() => {
    if (!externalStations || externalStations.length === 0) {
      // Only clear if we previously had external data (page navigating away)
      // The selectedDateRange being null signals we're not on a station-providing page
      if (!selectedDateRange) {
        setObservations(null);
      }
      return;
    }

    let result: StationData | null;
    if (selectedDateRange) {
      result = findLatestInRange(externalStations, selectedDateRange);
    } else if (selectedTimestamp) {
      result = findClosestObservation(externalStations, selectedTimestamp);
    } else {
      result = null;
    }
    setObservations(result);
    setError(null);
  }, [externalStations, selectedTimestamp, selectedDateRange, findClosestObservation, findLatestInRange]);

  // Fetch station observations callback
  const fetchStationObservations = useCallback(async () => {
    if (!selectedEvent || !selectedTimestamp) {
      setObservations(null);
      setError(null);
      return;
    }

    // If external stations data is available, use it instead of making an API call
    if (externalStations && externalStations.length > 0) {
      const result = selectedDateRange
        ? findLatestInRange(externalStations, selectedDateRange)
        : findClosestObservation(externalStations, selectedTimestamp);
      setObservations(result);
      setError(null);
      setLoading(false);
      return;
    }

    // If selectedDateRange is set, a page (Nowcasting/Observations) will provide external stations.
    // Don't do our own fetch — wait for them to arrive and avoid a stale async result overwriting.
    if (selectedDateRange) {
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const source = getEventSource(selectedEvent);
      const startTime = new Date(selectedTimestamp.getTime() - 5 * 60 * 1000);
      const endTime = new Date(selectedTimestamp.getTime() + 5 * 60 * 1000);

      const response = await stationObservationsService.getStationObservations(
        selectedEvent.season_year,
        selectedEvent.circuit_id,
        selectedEvent.start_date,
        source,
        startTime.toISOString(),
        endTime.toISOString()
      );

      const result = findClosestObservation(response.stations, selectedTimestamp);
      setObservations(result);
    } catch (err) {
      console.error('Error fetching station observations:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch observations');
      setObservations(null);
    } finally {
      setLoading(false);
    }
  }, [selectedEvent, selectedTimestamp, selectedDateRange, externalStations, findClosestObservation, findLatestInRange]);

  // Fetch station observations on mount/change
  useEffect(() => {
    fetchStationObservations();
  }, [fetchStationObservations]);

  // Fetch Tomorrow.io data
  const fetchTioData = useCallback(async () => {
    if (!selectedEvent || !isTioAvailable || !selectedTimestamp) {
      setTioData(null);
      setTioError(null);
      return;
    }

    const latitude = selectedEvent.circuit?.latitude;
    const longitude = selectedEvent.circuit?.longitude;

    if (!latitude || !longitude) {
      setTioError('Circuit coordinates not available');
      return;
    }

    setTioLoading(true);
    setTioError(null);

    try {
      // Pass the selected timestamp to fetch forecast for that specific time
      const weatherData = await tomorrowIoTimelineService.getCurrentWeather(
        latitude, 
        longitude,
        selectedTimestamp.toISOString()
      );
      setTioData(weatherData);
      setTioTimestamp(selectedTimestamp); // Use the selected timestamp
    } catch (err) {
      console.error('Error fetching Tomorrow.io data:', err);
      setTioError(err instanceof Error ? err.message : 'Failed to fetch weather data');
      setTioData(null);
    } finally {
      setTioLoading(false);
    }
  }, [selectedEvent, isTioAvailable, selectedTimestamp]);

  // Fetch TIO data when source is tio-api and selectedTimestamp changes
  useEffect(() => {
    if (dataSource === 'tio-api' && isTioAvailable && selectedTimestamp) {
      fetchTioData();
    }
  }, [dataSource, isTioAvailable, selectedTimestamp, fetchTioData]);

  // Refresh function to refetch current data source
  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      if (dataSource === 'tio-api') {
        await fetchTioData();
      } else {
        await fetchStationObservations();
      }
    } finally {
      setRefreshing(false);
    }
  }, [dataSource, fetchTioData, fetchStationObservations]);

  // Normalize weather data from either source
  const currentWeather: NormalizedWeatherData = useMemo(() => {
    if (dataSource === 'tio-api' && tioData) {
      return {
        temperature: tioData.temperature ?? null,
        trackTemperature: tioData.trackTemperature ?? null,
        lightIntensity: tioData.lightIntensity ?? null,
        wbgt: tioData.wbgt ?? null,
        heatIndex: tioData.heatIndex ?? null,
        timestamp: tioTimestamp,
      };
    }

    // Extract from station observations
    if (!observations || observations.observations.length === 0) {
      return {
        temperature: null,
        trackTemperature: null,
        lightIntensity: null,
        wbgt: null,
        heatIndex: null,
        timestamp: null,
      };
    }

    const observation = observations.observations[0];
    const getReading = (names: string[]): number | null => {
      for (const name of names) {
        const index = observations.reading_config.findIndex(cfg => cfg.name === name);
        if (index !== -1 && observation.readings[index] !== null) {
          return observation.readings[index] as number;
        }
      }
      return null;
    };

    return {
      temperature: getReading(['temperature']),
      trackTemperature: getReading(['trackTemperature']),
      lightIntensity: getReading(['lightIntensity']),
      wbgt: getReading(['wetBulbGlobeTemperature']),
      heatIndex: getReading(['heatIndex']),
      timestamp: new Date(observation.reading_timestamp),
    };
  }, [dataSource, tioData, tioTimestamp, observations]);

  const value: StationObservationsContextType = useMemo(
    () => ({
      observations,
      loading,
      error,
      tioData,
      tioLoading,
      tioError,
      tioTimestamp,
      dataSource,
      setDataSource,
      currentWeather,
      isTioAvailable,
      refresh,
      refreshing,
      setExternalStations,
      setSelectedDateRange,
    }),
    [observations, loading, error, tioData, tioLoading, tioError, tioTimestamp, dataSource, currentWeather, isTioAvailable, refresh, refreshing, setSelectedDateRange]
  );

  return <StationObservationsContext.Provider value={value}>{children}</StationObservationsContext.Provider>;
};
